// "Save to" controls shared by both tabs, plus the "it's saved" feedback: a toast naming the file
// and where it went, and a short green flash on the button that was pressed.
import { chooseSaveFolder, getSaveFolderName, getSaveMode, saveCapabilities, setSaveMode, type SaveMode } from '../io/save.js';
import { showToast } from './toast.js';

/** Markup for the Output section. `prefix` keeps ids unique per tab. */
export function saveLocationMarkup(prefix: string): string {
  return `
        <div class="field-row" id="${prefix}-save-mode-row">
          <label for="${prefix}-save-mode">Save to</label>
          <select id="${prefix}-save-mode">
            <option value="downloads">Downloads</option>
            <option value="ask">Ask me where, every time</option>
            <option value="folder">A folder I choose</option>
          </select>
        </div>
        <div class="field-row save-folder-row" id="${prefix}-save-folder-row" hidden>
          <span class="muted save-folder-name" id="${prefix}-save-folder-name">No folder chosen yet</span>
          <button type="button" class="btn btn-small" id="${prefix}-save-folder-pick">Choose folder…</button>
        </div>
        <p class="hint" id="${prefix}-save-hint" hidden></p>`;
}

/** Wires the controls; every tab shares the same stored choice, so changing it in one changes both. */
export function bindSaveLocation(panel: ParentNode, prefix: string): void {
  const row = panel.querySelector(`#${prefix}-save-mode-row`) as HTMLDivElement;
  const select = panel.querySelector(`#${prefix}-save-mode`) as HTMLSelectElement;
  const folderRow = panel.querySelector(`#${prefix}-save-folder-row`) as HTMLDivElement;
  const folderName = panel.querySelector(`#${prefix}-save-folder-name`) as HTMLSpanElement;
  const pickBtn = panel.querySelector(`#${prefix}-save-folder-pick`) as HTMLButtonElement;
  const hint = panel.querySelector(`#${prefix}-save-hint`) as HTMLParagraphElement;
  const caps = saveCapabilities();

  if (caps.platform === 'capacitor') {
    // iOS: the share sheet is the chooser (Save to Photos / Save to Files / AirDrop).
    row.hidden = true;
    hint.textContent = 'Exports open the share sheet, where you choose Save to Photos, Save to Files or another app.';
    hint.hidden = false;
    return;
  }
  const askOpt = select.querySelector('option[value="ask"]') as HTMLOptionElement;
  const folderOpt = select.querySelector('option[value="folder"]') as HTMLOptionElement;
  askOpt.hidden = !caps.canAsk;
  folderOpt.hidden = !caps.canFolder;
  if (!caps.canAsk && !caps.canFolder) {
    hint.textContent = 'This browser always saves to its own Downloads folder (change that in the browser settings). Chrome and Edge can ask where to save.';
    hint.hidden = false;
  }

  const refresh = () => {
    const mode = getSaveMode();
    select.value = mode;
    folderRow.hidden = mode !== 'folder';
    const name = getSaveFolderName();
    folderName.textContent = name ? `Saving into “${name}”` : 'No folder chosen yet';
  };
  select.addEventListener('change', async () => {
    const mode = select.value as SaveMode;
    if (mode === 'folder' && !getSaveFolderName()) {
      const picked = await chooseSaveFolder().catch((err: Error) => {
        showToast(err.message, true);
        return null;
      });
      if (!picked) {
        refresh();
        return;
      }
    }
    setSaveMode(mode);
    refresh();
    window.dispatchEvent(new CustomEvent('mpt-save-mode'));
  });
  pickBtn.addEventListener('click', async () => {
    const picked = await chooseSaveFolder().catch((err: Error) => {
      showToast(err.message, true);
      return null;
    });
    if (picked) {
      setSaveMode('folder');
      refresh();
      window.dispatchEvent(new CustomEvent('mpt-save-mode'));
    }
  });
  window.addEventListener('mpt-save-mode', refresh);
  refresh();
}

/** Tells the user the file is on its way: toast + a green "Saved ✓" flash on the button. */
export function announceSaved(button: HTMLButtonElement, filename: string, where: string): void {
  showToast(`✓ ${filename} saved to ${where}`);
  const original = button.dataset.label ?? button.textContent ?? '';
  button.dataset.label = original;
  button.classList.add('saved-flash');
  button.textContent = '✓ Saved';
  window.clearTimeout(Number(button.dataset.flashTimer));
  button.dataset.flashTimer = String(
    window.setTimeout(() => {
      button.classList.remove('saved-flash');
      button.textContent = button.dataset.label ?? original;
    }, 1800),
  );
}

export function announceCancelled(): void {
  showToast('Save cancelled — nothing was written');
}
